import AddIcon from '@mui/icons-material/Add';
import { i18n } from 'i18n';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import RemoveIcon from '@mui/icons-material/Remove';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { PDFDocument, PDFFont, PDFPage } from 'pdf-lib';
import React from 'react';
import excelTemplatesJson from './customReportExcelTemplates.json';

interface ExcelTemplateColumn {
  index: number;
  letter: string;
  width: number;
}

interface ExcelTemplateRow {
  index: number;
  height: number;
}

interface ExcelTemplateCell {
  ref: string;
  column: number;
  row: number;
  value: string;
  style: number;
}

interface ExcelTemplateMerge {
  startRef: string;
  startColumn: number;
  startRow: number;
  endColumn: number;
  endRow: number;
}

interface ExcelTemplateLogoArea {
  fromColumn: number;
  fromRow: number;
  toColumn: number;
  toRow: number;
  fromColumnOffset?: number;
  fromRowOffset?: number;
  toColumnOffset?: number;
  toRowOffset?: number;
}

interface ExcelTemplateStyle {
  fontWeight?: number;
  fontStyle?: string;
  fontSize?: number;
  textColor?: string;
  fillColor?: string;
  textAlign?: string;
  verticalAlign?: string;
  wrap?: boolean;
  borderLeft?: string;
  borderRight?: string;
  borderTop?: string;
  borderBottom?: string;
}

export interface CustomReportExcelTemplate {
  key: string;
  label: string;
  sheetName: string;
  assetFileName: string;
  outputFileName: string;
  aliases: string[];
  columns: ExcelTemplateColumn[];
  rows: ExcelTemplateRow[];
  merges: ExcelTemplateMerge[];
  cells: ExcelTemplateCell[];
  styles: ExcelTemplateStyle[];
  logoArea?: ExcelTemplateLogoArea;
  pdfBackground?: boolean;
}

type NullableExcelTemplateStyle = {
  [Key in keyof ExcelTemplateStyle]?: ExcelTemplateStyle[Key] | null;
};

type RawCustomReportExcelTemplate = Omit<CustomReportExcelTemplate, 'styles'> & {
  styles: NullableExcelTemplateStyle[];
};

interface RawCustomReportExcelTemplatesJson {
  templates: RawCustomReportExcelTemplate[];
}

export interface CustomReportExcelFormValues {
  cells: Record<string, string>;
  richCells?: Record<string, string>;
  logoDataUri?: string;
  logoScale?: number;
  pdfBackgroundDataUri?: string;
  pdfBackgroundFileName?: string;
  pdfPageWidth?: number;
  pdfPageHeight?: number;
}

interface CustomReportExcelTemplateEditorProps {
  template: CustomReportExcelTemplate;
  values: CustomReportExcelFormValues;
  onChange: (values: CustomReportExcelFormValues) => void;
  defaultLogoDataUri?: string;
  onLogoUploaded?: (logoDataUri: string) => Promise<void> | void;
}

interface EditableCellTextProps {
  cellRef: string;
  value: string;
  html?: string;
  onRichChange: (value: string, html: string) => void;
  cellWidth: number;
  cellHeight: number;
  css: ReturnType<typeof cssForStyle>;
}

type PdfLib = typeof import('pdf-lib');
type BorderSide = 'left' | 'right' | 'top' | 'bottom';
type ExcelTextAlign = 'left' | 'center' | 'right';

const EXCEL_POINT_TO_PIXEL = 4 / 3;
const EXCEL_COLUMN_WIDTH_SCALE = 1.35;
const SHEET_LEFT_GUTTER = 96;
const CHECKBOX_UNCHECKED = '\u2610';
const CHECKBOX_CHECKED = '\u2611';
const MIN_INLINE_FONT_SIZE = 8;
const MAX_INLINE_FONT_SIZE = 28;
const INLINE_FONT_SIZE_STEP = 2;
const MIN_LOGO_SCALE = 0.25;
const MAX_LOGO_SCALE = 2;
const LOGO_SCALE_STEP = 0.1;
const PDF_TEMPLATE_COLUMN_COUNT = 48;
const PDF_TEMPLATE_ROW_COUNT = 120;
const CSS_PIXEL_TO_PDF_POINT = 0.75;
const DEFAULT_TEXT_COLOR = '#111827';
const TEXT_COLOR_PRESETS = [
  '#111827', '#374151', '#6b7280', '#9ca3af', '#d1d5db', '#ffffff',
  '#dc2626', '#ea580c', '#d97706', '#ca8a04', '#65a30d', '#16a34a',
  '#059669', '#0891b2', '#0284c7', '#2563eb', '#4f46e5', '#7c3aed',
  '#9333ea', '#c026d3', '#db2777', '#e11d48', '#78350f', '#000000',
];

const normaliseNullable = <T extends unknown>(value: T | null | undefined) => value ?? undefined;

const normaliseExcelTemplateStyle = (style: NullableExcelTemplateStyle): ExcelTemplateStyle => ({
  fontWeight: normaliseNullable(style.fontWeight),
  fontStyle: normaliseNullable(style.fontStyle),
  fontSize: normaliseNullable(style.fontSize),
  textColor: normaliseNullable(style.textColor),
  fillColor: normaliseNullable(style.fillColor),
  textAlign: normaliseNullable(style.textAlign),
  verticalAlign: normaliseNullable(style.verticalAlign),
  wrap: normaliseNullable(style.wrap),
  borderLeft: normaliseNullable(style.borderLeft),
  borderRight: normaliseNullable(style.borderRight),
  borderTop: normaliseNullable(style.borderTop),
  borderBottom: normaliseNullable(style.borderBottom),
});

const rawCustomReportExcelTemplates = (excelTemplatesJson as unknown as RawCustomReportExcelTemplatesJson).templates;

export const customReportExcelTemplates: CustomReportExcelTemplate[] = rawCustomReportExcelTemplates.map((template) => ({
  ...template,
  styles: template.styles.map(normaliseExcelTemplateStyle),
}));

const fileStem = (fileName: string) => fileName.replace(/\.[^/.]+$/, '') || 'custom-report';

const columnLetter = (index: number) => {
  let letter = '';
  let remaining = index;
  while (remaining > 0) {
    const modulo = (remaining - 1) % 26;
    letter = String.fromCharCode(65 + modulo) + letter;
    remaining = Math.floor((remaining - modulo) / 26);
  }
  return letter;
};

export const createPdfBackedExcelTemplate = (
  fileName: string,
  pageWidth: number,
  pageHeight: number,
): CustomReportExcelTemplate => {
  const columns = Array.from({ length: PDF_TEMPLATE_COLUMN_COUNT }, (_, index) => {
    const columnIndex = index + 1;
    return {
      index: columnIndex,
      letter: columnLetter(columnIndex),
      width: (pageWidth / PDF_TEMPLATE_COLUMN_COUNT) / EXCEL_COLUMN_WIDTH_SCALE,
    };
  });
  const rows = Array.from({ length: PDF_TEMPLATE_ROW_COUNT }, (_, index) => ({
    index: index + 1,
    height: (pageHeight / PDF_TEMPLATE_ROW_COUNT) / EXCEL_POINT_TO_PIXEL,
  }));

  return {
    key: `uploaded_pdf_${fileStem(fileName).toLowerCase().replace(/[^a-z0-9]+/g, '_')}`,
    label: fileStem(fileName).replace(/[-_]+/g, ' '),
    sheetName: `${fileStem(fileName)} - PDF`,
    assetFileName: fileName,
    outputFileName: `${fileStem(fileName)}.pdf`,
    aliases: [fileName],
    columns,
    rows,
    merges: [],
    styles: [{
      fontSize: 10,
      textColor: '#111827',
      fillColor: 'transparent',
      wrap: false,
      verticalAlign: 'top',
    }],
    cells: rows.flatMap((row) => columns.map((column) => ({
      ref: `${column.letter}${row.index}`,
      column: column.index,
      row: row.index,
      value: '',
      style: 0,
    }))),
    pdfBackground: true,
  };
};

export const createPdfBackedExcelTemplateFromValues = (
  values?: CustomReportExcelFormValues,
  fallbackName = 'uploaded-pdf.pdf',
) => {
  if (!values?.pdfBackgroundDataUri || !values.pdfPageWidth || !values.pdfPageHeight) return undefined;
  return createPdfBackedExcelTemplate(
    values.pdfBackgroundFileName || fallbackName,
    values.pdfPageWidth,
    values.pdfPageHeight,
  );
};

const normaliseLookupValue = (value?: string) => (value ?? '')
  .toLowerCase()
  .replace(/\.[^.]+$/, '')
  .replace(/[^a-z0-9]+/g, '');

export const findCustomReportExcelTemplate = (value?: string) => {
  const normalised = normaliseLookupValue(value);
  if (!normalised) return undefined;

  return customReportExcelTemplates.find((template) => {
    const aliases = [
      template.key,
      template.label,
      template.sheetName,
      template.assetFileName,
      template.outputFileName,
      ...(template.aliases ?? []),
    ].map(normaliseLookupValue).filter(Boolean);

    return aliases.some((alias) => normalised === alias || normalised.includes(alias) || alias.includes(normalised));
  });
};

export const createCustomReportExcelFormValues = (
  template?: CustomReportExcelTemplate,
  logoDataUri?: string,
  pdfBackground?: Pick<
    CustomReportExcelFormValues,
    'pdfBackgroundDataUri' | 'pdfBackgroundFileName' | 'pdfPageWidth' | 'pdfPageHeight'
  >,
): CustomReportExcelFormValues => ({
  cells: template?.pdfBackground
    ? {}
    : (template?.cells ?? []).reduce<Record<string, string>>((result, cell) => ({
      ...result,
      [cell.ref]: cell.value ?? '',
    }), {}),
  richCells: {},
  logoDataUri,
  ...(pdfBackground ?? {}),
});

export const mergeCustomReportExcelFormValues = (
  template?: CustomReportExcelTemplate,
  values?: Partial<CustomReportExcelFormValues>,
  logoDataUri?: string,
): CustomReportExcelFormValues => {
  const base = createCustomReportExcelFormValues(template, logoDataUri);
  if (!values) return base;

  return {
    cells: {
      ...base.cells,
      ...(values.cells ?? {}),
    },
    richCells: {
      ...(values.richCells ?? {}),
    },
    logoDataUri: values.logoDataUri ?? logoDataUri,
    logoScale: values.logoScale,
    pdfBackgroundDataUri: values.pdfBackgroundDataUri,
    pdfBackgroundFileName: values.pdfBackgroundFileName,
    pdfPageWidth: values.pdfPageWidth,
    pdfPageHeight: values.pdfPageHeight,
  };
};

const cellRecord = (template: CustomReportExcelTemplate) => template.cells.reduce<Record<string, ExcelTemplateCell>>(
  (result, cell) => ({
    ...result,
    [cell.ref]: cell,
  }),
  {},
);

const rowHeight = (row: ExcelTemplateRow) => row.height * EXCEL_POINT_TO_PIXEL;
const columnWidth = (column: ExcelTemplateColumn) => column.width * EXCEL_COLUMN_WIDTH_SCALE;
const fontSizePx = (style?: ExcelTemplateStyle) => (style?.fontSize ?? 10) * EXCEL_POINT_TO_PIXEL;

const totalSheetWidth = (template: CustomReportExcelTemplate) => template.columns.reduce((sum, column) => sum + columnWidth(column), 0);
const totalDisplayWidth = (template: CustomReportExcelTemplate) => totalSheetWidth(template) + SHEET_LEFT_GUTTER;
const totalSheetHeight = (template: CustomReportExcelTemplate) => template.rows.reduce((sum, row) => sum + rowHeight(row), 0);

const columnOffset = (template: CustomReportExcelTemplate, columnIndex: number) => template.columns
  .filter((column) => column.index < columnIndex)
  .reduce((sum, column) => sum + columnWidth(column), 0);

const rowOffset = (template: CustomReportExcelTemplate, rowIndex: number) => template.rows
  .filter((row) => row.index < rowIndex)
  .reduce((sum, row) => sum + rowHeight(row), 0);

const refFor = (column: ExcelTemplateColumn, row: ExcelTemplateRow) => `${column.letter}${row.index}`;

const cellRefAtPoint = (template: CustomReportExcelTemplate, x: number, y: number) => {
  const column = template.columns.find((item) => {
    const left = columnOffset(template, item.index);
    return x >= left && x < left + columnWidth(item);
  }) ?? template.columns[template.columns.length - 1];
  const row = template.rows.find((item) => {
    const top = rowOffset(template, item.index);
    return y >= top && y < top + rowHeight(item);
  }) ?? template.rows[template.rows.length - 1];

  if (!column || !row) return undefined;
  return refFor(column, row);
};

const mergeForStart = (template: CustomReportExcelTemplate, cellRef: string) => (
  template.merges.find((merge) => merge.startRef === cellRef)
);

const isCoveredByMerge = (template: CustomReportExcelTemplate, columnIndex: number, rowIndex: number) => (
  template.merges.some((merge) => (
    !(merge.startColumn === columnIndex && merge.startRow === rowIndex)
    && columnIndex >= merge.startColumn
    && columnIndex <= merge.endColumn
    && rowIndex >= merge.startRow
    && rowIndex <= merge.endRow
  ))
);

const borderWidth = (style?: string) => {
  if (!style) return 0;
  if (style === 'medium') return 2;
  if (style === 'thick') return 3;
  return 1;
};

const textAlign = (value?: string): ExcelTextAlign => {
  if (value === 'center' || value === 'right') return value;
  return 'left';
};

const cssForStyle = (style?: ExcelTemplateStyle) => ({
  borderLeft: borderWidth(style?.borderLeft) ? `${borderWidth(style?.borderLeft)}px solid #111827` : 'none',
  borderRight: borderWidth(style?.borderRight) ? `${borderWidth(style?.borderRight)}px solid #111827` : 'none',
  borderTop: borderWidth(style?.borderTop) ? `${borderWidth(style?.borderTop)}px solid #111827` : 'none',
  borderBottom: borderWidth(style?.borderBottom) ? `${borderWidth(style?.borderBottom)}px solid #111827` : 'none',
  backgroundColor: style?.fillColor ?? '#fff',
  color: style?.textColor ?? '#111827',
  fontWeight: style?.fontWeight ?? 400,
  fontStyle: style?.fontStyle ?? 'normal',
  fontSize: fontSizePx(style),
  textAlign: textAlign(style?.textAlign),
  verticalAlign: style?.verticalAlign,
  whiteSpace: style?.wrap ? 'pre-wrap' : 'nowrap',
});

const isSectionTitleCell = (value: string, style?: ExcelTemplateStyle) => (
  Boolean(value.trim())
  && Boolean(style?.fontWeight)
  && (style?.fontSize ?? 0) >= 14
);

const readFileAsDataUri = (file: File) => new Promise<string>((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    if (typeof reader.result === 'string') {
      resolve(reader.result);
      return;
    }

    reject(new Error('Unable to read logo file.'));
  };
  reader.onerror = () => reject(reader.error ?? new Error('Unable to read logo file.'));
  reader.readAsDataURL(file);
});

const dataUriToBytes = (dataUri: string) => {
  const [, base64 = ''] = dataUri.split(',');
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
};

const dataUriToBlob = (dataUri: string) => {
  const [metadata = ''] = dataUri.split(',');
  const contentType = metadata.match(/^data:([^;]+);/)?.[1] ?? 'application/octet-stream';
  return new Blob([dataUriToBytes(dataUri)], { type: contentType });
};

const clampInlineFontSize = (value: number) => Math.max(
  MIN_INLINE_FONT_SIZE,
  Math.min(MAX_INLINE_FONT_SIZE, Math.round(value)),
);

const clampLogoScale = (value?: number) => Math.max(
  MIN_LOGO_SCALE,
  Math.min(MAX_LOGO_SCALE, value ?? 1),
);

const normaliseLogoScale = (value?: number) => Number(clampLogoScale(value).toFixed(2));

const scaleLogoRect = (rect: { left: number; top: number; width: number; height: number }, scale: number) => {
  const nextWidth = rect.width * scale;
  const nextHeight = rect.height * scale;
  return {
    left: rect.left + (rect.width - nextWidth) / 2,
    top: rect.top + (rect.height - nextHeight) / 2,
    width: nextWidth,
    height: nextHeight,
  };
};

const logoRect = (template: CustomReportExcelTemplate) => {
  if (!template.logoArea) return undefined;

  const left = columnOffset(template, template.logoArea.fromColumn) + (template.logoArea.fromColumnOffset ?? 0) * EXCEL_COLUMN_WIDTH_SCALE;
  const top = rowOffset(template, template.logoArea.fromRow) + (template.logoArea.fromRowOffset ?? 0);
  const right = columnOffset(template, template.logoArea.toColumn) + (template.logoArea.toColumnOffset ?? 0) * EXCEL_COLUMN_WIDTH_SCALE;
  const bottom = rowOffset(template, template.logoArea.toRow) + (template.logoArea.toRowOffset ?? 0);

  return {
    left,
    top,
    width: Math.max(1, right - left),
    height: Math.max(1, bottom - top),
  };
};

const hexToRgb = (hex?: string) => {
  if (!hex || !/^#[0-9a-f]{6}$/i.test(hex)) return undefined;
  const value = hex.slice(1);
  return {
    r: parseInt(value.slice(0, 2), 16) / 255,
    g: parseInt(value.slice(2, 4), 16) / 255,
    b: parseInt(value.slice(4, 6), 16) / 255,
  };
};

const escapeHtml = (value: string) => value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const htmlFromText = (value: string) => escapeHtml(value).replace(/\r\n|\r|\n/g, '<br>');

const normaliseCssColor = (value?: string) => {
  if (!value) return undefined;
  const trimmed = value.trim().toLowerCase();
  if (/^#[0-9a-f]{6}$/i.test(trimmed)) return trimmed;
  if (/^#[0-9a-f]{3}$/i.test(trimmed)) {
    return `#${trimmed[1]}${trimmed[1]}${trimmed[2]}${trimmed[2]}${trimmed[3]}${trimmed[3]}`;
  }

  const rgb = trimmed.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!rgb) return undefined;
  const toHex = (component: string) => Math.max(0, Math.min(255, Number(component)))
    .toString(16)
    .padStart(2, '0');
  return `#${toHex(rgb[1])}${toHex(rgb[2])}${toHex(rgb[3])}`;
};

const normaliseCssFontSize = (value?: string) => {
  if (!value) return undefined;
  const match = value.trim().match(/^(\d+(?:\.\d+)?)(px|pt)?$/i);
  if (!match) return undefined;

  const numericValue = Number(match[1]);
  if (!Number.isFinite(numericValue)) return undefined;
  return match[2]?.toLowerCase() === 'pt'
    ? numericValue * EXCEL_POINT_TO_PIXEL
    : numericValue;
};

interface RichTextRun {
  text: string;
  color?: string;
  fontSizePx?: number;
}

const htmlToRichLines = (html: string, fallbackColor?: string, fallbackFontSizePx?: number) => {
  const container = document.createElement('div');
  container.innerHTML = html;
  const lines: RichTextRun[][] = [[]];
  const currentLine = () => lines[lines.length - 1];
  const addLine = () => {
    if (currentLine().length > 0) {
      lines.push([]);
    }
  };
  const addText = (text: string, color?: string, activeFontSizePx?: number) => {
    text.replace(/\u00a0/g, ' ').split(/\r\n|\r|\n/).forEach((part, index) => {
      if (index > 0) {
        lines.push([]);
      }
      if (part) {
        currentLine().push({ text: part, color, fontSizePx: activeFontSizePx });
      }
    });
  };
  const walk = (node: Node, color?: string, activeFontSizePx?: number) => {
    if (node.nodeType === Node.TEXT_NODE) {
      addText(node.textContent ?? '', color, activeFontSizePx);
      return;
    }

    if (!(node instanceof HTMLElement)) return;

    if (node.tagName === 'BR') {
      lines.push([]);
      return;
    }

    const nextColor = normaliseCssColor(node.style.color || node.getAttribute('color') || undefined) ?? color;
    const nextFontSize = normaliseCssFontSize(node.style.fontSize) ?? activeFontSizePx;
    const isBlock = node.tagName === 'DIV' || node.tagName === 'P';
    if (isBlock) addLine();
    Array.from(node.childNodes).forEach((child) => walk(child, nextColor, nextFontSize));
    if (isBlock) addLine();
  };

  Array.from(container.childNodes).forEach((child) => (
    walk(child, normaliseCssColor(fallbackColor), fallbackFontSizePx)
  ));

  while (lines.length > 1 && lines[lines.length - 1].length === 0) {
    lines.pop();
  }

  return lines;
};

const drawBorder = (
  page: PDFPage,
  x: number,
  y: number,
  width: number,
  height: number,
  side: BorderSide,
  border: string | undefined,
  pdfLib: PdfLib,
) => {
  const thickness = borderWidth(border);
  if (!thickness) return;

  const start = {
    left: { x, y },
    right: { x: x + width, y },
    top: { x, y: y + height },
    bottom: { x, y },
  }[side];
  const end = {
    left: { x, y: y + height },
    right: { x: x + width, y: y + height },
    top: { x: x + width, y: y + height },
    bottom: { x: x + width, y },
  }[side];

  page.drawLine({
    start,
    end,
    thickness: Math.max(0.5, thickness * 0.65),
    color: pdfLib.rgb(0, 0, 0),
  });
};

const wrapText = (text: string, font: PDFFont, fontSize: number, maxWidth: number) => {
  const rawLines = text.replace(/\r\n/g, '\n').split('\n');

  return rawLines.flatMap((rawLine) => {
    const words = rawLine.replace(/\s+/g, ' ').trim().split(' ').filter(Boolean);
    if (words.length === 0) return [''];

    return words.reduce<string[]>((lines, word) => {
      const currentLine = lines[lines.length - 1] ?? '';
      const nextLine = currentLine ? `${currentLine} ${word}` : word;
      if (font.widthOfTextAtSize(nextLine, fontSize) <= maxWidth || currentLine.length === 0) {
        return [...lines.slice(0, -1), nextLine];
      }

      return [...lines, word];
    }, ['']);
  });
};

type PdfTextSegment =
  | { type: 'text'; text: string }
  | { type: 'checkbox'; checked: boolean };

const splitPdfTextSegments = (text: string) => {
  const segments: PdfTextSegment[] = [];
  let buffer = '';

  Array.from(text).forEach((char) => {
    if (char !== CHECKBOX_UNCHECKED && char !== CHECKBOX_CHECKED) {
      buffer += char;
      return;
    }

    if (buffer) {
      segments.push({ type: 'text', text: buffer });
      buffer = '';
    }
    segments.push({ type: 'checkbox', checked: char === CHECKBOX_CHECKED });
  });

  if (buffer) {
    segments.push({ type: 'text', text: buffer });
  }

  return segments;
};

const checkboxAdvance = (fontSize: number) => fontSize * 1.05;
const checkboxSize = (fontSize: number) => fontSize * 0.72;

const richRunWidth = (run: RichTextRun, font: PDFFont, fontSize: number) => splitPdfTextSegments(run.text)
  .reduce((sum, segment) => (
    segment.type === 'checkbox'
      ? sum + checkboxAdvance(fontSize)
      : sum + font.widthOfTextAtSize(segment.text, fontSize)
  ), 0);

const drawPdfCheckbox = (
  page: PDFPage,
  x: number,
  baselineY: number,
  fontSize: number,
  checked: boolean,
  color: { r: number; g: number; b: number },
  pdfLib: PdfLib,
) => {
  const size = checkboxSize(fontSize);
  const bottom = baselineY + fontSize * 0.04;
  const strokeColor = pdfLib.rgb(color.r, color.g, color.b);

  page.drawRectangle({
    x,
    y: bottom,
    width: size,
    height: size,
    borderColor: strokeColor,
    borderWidth: Math.max(0.55, fontSize * 0.055),
  });

  if (!checked) return;

  page.drawLine({
    start: { x: x + size * 0.2, y: bottom + size * 0.48 },
    end: { x: x + size * 0.42, y: bottom + size * 0.23 },
    thickness: Math.max(0.75, fontSize * 0.08),
    color: strokeColor,
  });
  page.drawLine({
    start: { x: x + size * 0.42, y: bottom + size * 0.23 },
    end: { x: x + size * 0.82, y: bottom + size * 0.78 },
    thickness: Math.max(0.75, fontSize * 0.08),
    color: strokeColor,
  });
};

const drawCellText = (
  page: PDFPage,
  text: string,
  richHtml: string | undefined,
  x: number,
  y: number,
  width: number,
  height: number,
  style: ExcelTemplateStyle | undefined,
  font: PDFFont,
  boldFont: PDFFont,
  italicFont: PDFFont,
  italicBoldFont: PDFFont,
  pdfLib: PdfLib,
  sheetScale: number,
) => {
  if (!text.trim()) return;

  let targetFont = font;
  if (style?.fontStyle === 'italic' && style.fontWeight) {
    targetFont = italicBoldFont;
  } else if (style?.fontStyle === 'italic') {
    targetFont = italicFont;
  } else if (style?.fontWeight) {
    targetFont = boldFont;
  }

  const baseFontSizePx = fontSizePx(style);
  const fontSize = Math.max(4.5, baseFontSizePx * sheetScale);
  const padding = 2;
  const richLines = richHtml ? htmlToRichLines(richHtml, style?.textColor, baseFontSizePx) : undefined;
  const plainLines: RichTextRun[][] = (style?.wrap
    ? wrapText(text, targetFont, fontSize, Math.max(1, width - padding * 2))
    : text.replace(/\r\n/g, '\n').split('\n'))
    .map((line) => [{ text: line, color: style?.textColor }]);
  const lines: RichTextRun[][] = richLines
    ?? plainLines;
  const maxLines = style?.wrap
    ? Math.max(1, Math.floor(Math.max(fontSize * 1.15, height - padding * 2) / (fontSize * 1.15)))
    : lines.length;
  const visibleLines = lines.slice(0, maxLines);
  const fontSizeForRun = (run: RichTextRun) => Math.max(4.5, (run.fontSizePx ?? baseFontSizePx) * sheetScale);
  const lineHeight = Math.max(
    fontSize,
    ...visibleLines.flatMap((line) => line.map(fontSizeForRun)),
  ) * 1.15;
  const textHeight = visibleLines.length * lineHeight;
  const startY = style?.verticalAlign === 'center'
    ? y + (height + textHeight) / 2 - fontSize
    : y + height - padding - fontSize;
  const textColor = hexToRgb(style?.textColor) ?? { r: 0, g: 0, b: 0 };
  const align = textAlign(style?.textAlign);

  visibleLines.forEach((line, index) => {
    const lineWidth = line.reduce((sum, run) => sum + richRunWidth(run, targetFont, fontSizeForRun(run)), 0);
    let textX = x + padding;
    if (align === 'center') {
      textX = x + (width - lineWidth) / 2;
    } else if (align === 'right') {
      textX = x + width - lineWidth - padding;
    }

    line.forEach((run) => {
      const runColor = hexToRgb(run.color) ?? textColor;
      const runFontSize = fontSizeForRun(run);
      splitPdfTextSegments(run.text).forEach((segment) => {
        if (segment.type === 'checkbox') {
          drawPdfCheckbox(
            page,
            textX,
            startY - index * lineHeight,
            runFontSize,
            segment.checked,
            runColor,
            pdfLib,
          );
          textX += checkboxAdvance(runFontSize);
          return;
        }

        page.drawText(segment.text, {
          x: textX,
          y: startY - index * lineHeight,
          size: runFontSize,
          font: targetFont,
          color: pdfLib.rgb(runColor.r, runColor.g, runColor.b),
        });
        textX += targetFont.widthOfTextAtSize(segment.text, runFontSize);
      });
    });
  });
};

const drawLogo = async (
  pdfDoc: PDFDocument,
  page: PDFPage,
  logoSource: string | undefined,
  x: number,
  y: number,
  width: number,
  height: number,
) => {
  if (!logoSource) return;

  try {
    const response = await fetch(logoSource);
    const bytes = await response.arrayBuffer();
    const contentType = response.headers.get('content-type') ?? '';
    const isPng = contentType.includes('png') || logoSource.startsWith('data:image/png') || logoSource.toLowerCase().endsWith('.png');
    const image = isPng ? await pdfDoc.embedPng(bytes) : await pdfDoc.embedJpg(bytes);
    const scale = Math.min(width / image.width, height / image.height);
    const imageWidth = image.width * scale;
    const imageHeight = image.height * scale;

    page.drawImage(image, {
      x: x + (width - imageWidth) / 2,
      y: y + (height - imageHeight) / 2,
      width: imageWidth,
      height: imageHeight,
    });
  } catch {
    // Keep export usable if the logo cannot be embedded.
  }
};

const drawTemplateCellsOnPdfPage = (
  template: CustomReportExcelTemplate,
  values: CustomReportExcelFormValues,
  page: PDFPage,
  font: PDFFont,
  boldFont: PDFFont,
  italicFont: PDFFont,
  italicBoldFont: PDFFont,
  pdfLib: PdfLib,
  xOffset: number,
  yTop: number,
  scaleX: number,
  scaleY: number,
) => {
  const cellsByRef = cellRecord(template);
  const sheetScale = Math.min(scaleX, scaleY) * (template.pdfBackground ? CSS_PIXEL_TO_PDF_POINT : 1);

  template.rows.forEach((row) => {
    if (row.height <= 0) return;

    template.columns.forEach((column) => {
      if (column.width <= 0) return;
      const ref = refFor(column, row);
      if (isCoveredByMerge(template, column.index, row.index)) return;

      const cellText = values.cells[ref] ?? '';
      const richHtml = values.richCells?.[ref];
      if (template.pdfBackground && !cellText.trim() && !richHtml?.trim()) return;

      const merge = mergeForStart(template, ref);
      const columnSpan = merge ? merge.endColumn - merge.startColumn + 1 : 1;
      const rowSpan = merge ? merge.endRow - merge.startRow + 1 : 1;
      const width = template.columns
        .filter((item) => item.index >= column.index && item.index < column.index + columnSpan)
        .reduce((sum, item) => sum + columnWidth(item), 0) * scaleX;
      const height = template.rows
        .filter((item) => item.index >= row.index && item.index < row.index + rowSpan)
        .reduce((sum, item) => sum + rowHeight(item), 0) * scaleY;
      const x = xOffset + columnOffset(template, column.index) * scaleX;
      const y = yTop - (rowOffset(template, row.index) * scaleY) - height;
      const cell = cellsByRef[ref];
      const style = template.styles[cell?.style ?? 0];
      const fill = hexToRgb(style?.fillColor);

      if (fill && style?.fillColor?.toUpperCase() !== '#FFFFFF') {
        page.drawRectangle({
          x,
          y,
          width,
          height,
          color: pdfLib.rgb(fill.r, fill.g, fill.b),
        });
      }

      drawBorder(page, x, y, width, height, 'left', style?.borderLeft, pdfLib);
      drawBorder(page, x, y, width, height, 'right', style?.borderRight, pdfLib);
      drawBorder(page, x, y, width, height, 'top', style?.borderTop, pdfLib);
      drawBorder(page, x, y, width, height, 'bottom', style?.borderBottom, pdfLib);
      drawCellText(
        page,
        cellText,
        richHtml,
        x,
        y,
        width,
        height,
        style,
        font,
        boldFont,
        italicFont,
        italicBoldFont,
        pdfLib,
        sheetScale,
      );
    });
  });
};

const buildPdfBackedCustomReportExcelTemplatePdfBlob = async (
  template: CustomReportExcelTemplate,
  values: CustomReportExcelFormValues,
): Promise<Blob> => {
  const pdfLib = await import('pdf-lib');
  const sourcePdf = await pdfLib.PDFDocument.load(dataUriToBytes(values.pdfBackgroundDataUri ?? ''));
  const pdfDoc = await pdfLib.PDFDocument.create();
  const copiedPages = await pdfDoc.copyPages(sourcePdf, sourcePdf.getPageIndices());
  copiedPages.forEach((page) => pdfDoc.addPage(page));

  const page = pdfDoc.getPage(0);
  const font = await pdfDoc.embedFont(pdfLib.StandardFonts.Helvetica);
  const boldFont = await pdfDoc.embedFont(pdfLib.StandardFonts.HelveticaBold);
  const italicFont = await pdfDoc.embedFont(pdfLib.StandardFonts.HelveticaOblique);
  const italicBoldFont = await pdfDoc.embedFont(pdfLib.StandardFonts.HelveticaBoldOblique);
  const sheetWidth = totalSheetWidth(template);
  const sheetHeight = totalSheetHeight(template);

  drawTemplateCellsOnPdfPage(
    template,
    values,
    page,
    font,
    boldFont,
    italicFont,
    italicBoldFont,
    pdfLib,
    0,
    page.getHeight(),
    page.getWidth() / Math.max(1, sheetWidth),
    page.getHeight() / Math.max(1, sheetHeight),
  );

  const pdfBytes = await pdfDoc.save();
  return new Blob([new Uint8Array(pdfBytes)], { type: 'application/pdf' });
};

export const buildCustomReportExcelTemplatePdfBlob = async (
  template: CustomReportExcelTemplate,
  values: CustomReportExcelFormValues,
  fallbackLogoDataUri?: string,
): Promise<Blob> => {
  if (values.pdfBackgroundDataUri) {
    return buildPdfBackedCustomReportExcelTemplatePdfBlob(template, values);
  }

  const pdfLib = await import('pdf-lib');
  const pdfDoc = await pdfLib.PDFDocument.create();
  const font = await pdfDoc.embedFont(pdfLib.StandardFonts.Helvetica);
  const boldFont = await pdfDoc.embedFont(pdfLib.StandardFonts.HelveticaBold);
  const italicFont = await pdfDoc.embedFont(pdfLib.StandardFonts.HelveticaOblique);
  const italicBoldFont = await pdfDoc.embedFont(pdfLib.StandardFonts.HelveticaBoldOblique);
  const sheetWidth = totalDisplayWidth(template);
  const sheetHeight = totalSheetHeight(template);
  const isWide = sheetWidth > sheetHeight * 1.1;
  const isVeryWide = sheetWidth > 1200;
  let pageSize: [number, number] = [595.28, 841.89];
  if (isWide) {
    pageSize = isVeryWide ? [1190.55, 841.89] : [841.89, 595.28];
  }
  const page = pdfDoc.addPage(pageSize);
  const margin = 20;
  const scale = Math.min((page.getWidth() - margin * 2) / sheetWidth, (page.getHeight() - margin * 2) / sheetHeight);
  const top = page.getHeight() - margin;
  const cellsByRef = cellRecord(template);

  template.rows.forEach((row) => {
    if (row.height <= 0) return;

    template.columns.forEach((column) => {
      if (column.width <= 0) return;
      const ref = refFor(column, row);
      if (isCoveredByMerge(template, column.index, row.index)) return;

      const merge = mergeForStart(template, ref);
      const columnSpan = merge ? merge.endColumn - merge.startColumn + 1 : 1;
      const rowSpan = merge ? merge.endRow - merge.startRow + 1 : 1;
      const width = template.columns
        .filter((item) => item.index >= column.index && item.index < column.index + columnSpan)
        .reduce((sum, item) => sum + columnWidth(item), 0) * scale;
      const height = template.rows
        .filter((item) => item.index >= row.index && item.index < row.index + rowSpan)
        .reduce((sum, item) => sum + rowHeight(item), 0) * scale;
      const x = margin + (SHEET_LEFT_GUTTER + columnOffset(template, column.index)) * scale;
      const y = top - (rowOffset(template, row.index) * scale) - height;
      const cell = cellsByRef[ref];
      const style = template.styles[cell?.style ?? 0];
      const fill = hexToRgb(style?.fillColor);

      if (fill && style?.fillColor?.toUpperCase() !== '#FFFFFF') {
        page.drawRectangle({
          x,
          y,
          width,
          height,
          color: pdfLib.rgb(fill.r, fill.g, fill.b),
        });
      }

      drawBorder(page, x, y, width, height, 'left', style?.borderLeft, pdfLib);
      drawBorder(page, x, y, width, height, 'right', style?.borderRight, pdfLib);
      drawBorder(page, x, y, width, height, 'top', style?.borderTop, pdfLib);
      drawBorder(page, x, y, width, height, 'bottom', style?.borderBottom, pdfLib);
      drawCellText(
        page,
        values.cells[ref] ?? '',
        values.richCells?.[ref],
        x,
        y,
        width,
        height,
        style,
        font,
        boldFont,
        italicFont,
        italicBoldFont,
        pdfLib,
        scale,
      );
    });
  });

  const logo = logoRect(template);
  if (logo) {
    const scaledLogo = scaleLogoRect(logo, normaliseLogoScale(values.logoScale));
    await drawLogo(
      pdfDoc,
      page,
      values.logoDataUri || fallbackLogoDataUri,
      margin + (SHEET_LEFT_GUTTER + scaledLogo.left) * scale,
      top - (scaledLogo.top + scaledLogo.height) * scale,
      scaledLogo.width * scale,
      scaledLogo.height * scale,
    );
  }

  const pdfBytes = await pdfDoc.save();
  return new Blob([new Uint8Array(pdfBytes)], { type: 'application/pdf' });
};

const excelBorderStyle = (border?: string) => (
  borderWidth(border) ? `${borderWidth(border)}px solid #111827` : 'none'
);

const excelCellStyle = (
  style: ExcelTemplateStyle | undefined,
  width: number,
  height: number,
) => [
  `width:${Math.max(1, width)}px`,
  `height:${Math.max(1, height)}px`,
  `min-width:${Math.max(1, width)}px`,
  'font-family:Arial, sans-serif',
  `font-size:${fontSizePx(style)}px`,
  `font-weight:${style?.fontWeight ?? 400}`,
  `font-style:${style?.fontStyle ?? 'normal'}`,
  `color:${style?.textColor ?? '#111827'}`,
  `background-color:${style?.fillColor ?? '#fff'}`,
  `text-align:${textAlign(style?.textAlign)}`,
  `vertical-align:${style?.verticalAlign === 'center' ? 'middle' : 'top'}`,
  `white-space:${style?.wrap ? 'normal' : 'nowrap'}`,
  `border-left:${excelBorderStyle(style?.borderLeft)}`,
  `border-right:${excelBorderStyle(style?.borderRight)}`,
  `border-top:${excelBorderStyle(style?.borderTop)}`,
  `border-bottom:${excelBorderStyle(style?.borderBottom)}`,
  'padding:1px 2px',
].join(';');

export const buildCustomReportExcelTemplateWorkbookBlob = (
  template: CustomReportExcelTemplate,
  values: CustomReportExcelFormValues,
) => {
  const cellsByRef = cellRecord(template);
  const rows = template.rows
    .filter((row) => row.height > 0)
    .map((row) => {
      const cells = template.columns
        .filter((column) => column.width > 0)
        .map((column) => {
          const ref = refFor(column, row);
          if (isCoveredByMerge(template, column.index, row.index)) return '';

          const merge = mergeForStart(template, ref);
          const columnSpan = merge ? merge.endColumn - merge.startColumn + 1 : 1;
          const rowSpan = merge ? merge.endRow - merge.startRow + 1 : 1;
          const width = template.columns
            .filter((item) => item.index >= column.index && item.index < column.index + columnSpan)
            .reduce((sum, item) => sum + columnWidth(item), 0);
          const height = template.rows
            .filter((item) => item.index >= row.index && item.index < row.index + rowSpan)
            .reduce((sum, item) => sum + rowHeight(item), 0);
          const cell = cellsByRef[ref];
          const style = template.styles[cell?.style ?? 0];
          const html = values.richCells?.[ref] ?? htmlFromText(values.cells[ref] ?? '');
          const spanAttributes = [
            columnSpan > 1 ? `colspan="${columnSpan}"` : '',
            rowSpan > 1 ? `rowspan="${rowSpan}"` : '',
          ].filter(Boolean).join(' ');

          return `<td ${spanAttributes} style="${excelCellStyle(style, width, height)}">${html || '&nbsp;'}</td>`;
        })
        .join('');

      return `<tr style="height:${rowHeight(row)}px">${cells}</tr>`;
    })
    .join('');
  const columns = template.columns
    .filter((column) => column.width > 0)
    .map((column) => `<col style="width:${columnWidth(column)}px" />`)
    .join('');
  const workbook = [
    '<!doctype html>',
    '<html>',
    '<head>',
    '<meta charset="utf-8" />',
    `<title>${escapeHtml(template.label)}</title>`,
    '</head>',
    '<body>',
    '<table cellspacing="0" cellpadding="0" style="border-collapse:collapse;table-layout:fixed">',
    `<colgroup>${columns}</colgroup>`,
    rows,
    '</table>',
    '</body>',
    '</html>',
  ].join('');

  return new Blob([`\uFEFF${workbook}`], { type: 'application/vnd.ms-excel;charset=utf-8' });
};

const EditableCellText = ({
  cellRef,
  value,
  html,
  onRichChange,
  cellWidth,
  cellHeight,
  css,
}: EditableCellTextProps) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const isWrapped = css.whiteSpace === 'pre-wrap';
  const lineHeight = css.fontSize * 1.1;
  const textLineCount = Math.max(1, value.split('\n').length);
  const verticalPadding = css.verticalAlign === 'center'
    ? Math.max(1, (cellHeight - lineHeight * textLineCount) / 2)
    : 1;
  const baseStyle: React.CSSProperties = {
    minWidth: cellWidth,
    minHeight: cellHeight,
    boxSizing: 'border-box',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    color: css.color,
    fontFamily: 'Arial, sans-serif',
    fontSize: css.fontSize,
    fontWeight: css.fontWeight,
    fontStyle: css.fontStyle,
    textAlign: css.textAlign,
    whiteSpace: isWrapped ? 'pre-wrap' : 'pre',
    overflow: 'visible',
    padding: `${verticalPadding}px 2px 1px`,
    lineHeight: 1.1,
  };

  React.useEffect(() => {
    if (!ref.current || document.activeElement === ref.current) return;
    const nextHtml = html ?? htmlFromText(value);
    if (ref.current.innerHTML === nextHtml) return;
    ref.current.innerHTML = nextHtml;
  }, [html, value]);

  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault();
    document.execCommand('insertText', false, event.clipboardData.getData('text/plain'));
  };

  const handleInput = (event: React.FormEvent<HTMLDivElement>) => {
    onRichChange(
      event.currentTarget.innerText.replace(/\u00a0/g, ' '),
      event.currentTarget.innerHTML,
    );
  };

  if (isWrapped) {
    return (
      <div
        ref={ref}
        data-custom-report-cell-ref={cellRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onPaste={handlePaste}
        style={{
          ...baseStyle,
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          wordBreak: 'normal',
        }}
      />
    );
  }

  let horizontalStyle: React.CSSProperties = { position: 'absolute', left: 0, top: 0, width: 'max-content' };
  if (css.textAlign === 'right') {
    horizontalStyle = { position: 'absolute', right: 2, top: 0, width: 'max-content' };
  } else if (css.textAlign === 'center') {
    horizontalStyle = { position: 'absolute', left: '50%', top: 0, width: 'max-content', transform: 'translateX(-50%)' };
  }

  return (
    <div
      ref={ref}
      data-custom-report-cell-ref={cellRef}
      contentEditable
      suppressContentEditableWarning
      onInput={handleInput}
      onPaste={handlePaste}
      style={{
        ...baseStyle,
        ...horizontalStyle,
        height: cellHeight,
      }}
    />
  );
};

interface ColorPickerButtonProps {
  value: string;
  onPick: (color: string) => void;
}

const ColorPickerButton = ({ value, onPick }: ColorPickerButtonProps) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const currentValue = (value || DEFAULT_TEXT_COLOR).toLowerCase();

  return (
    <>
      <Tooltip title={i18n._('Text colour')}>
        <IconButton
          size="small"
          color="secondary"
          onMouseDown={(event) => event.preventDefault()}
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{ borderRadius: 1, px: 0.5 }}
        >
          <Stack alignItems="center" spacing={0.25} sx={{ lineHeight: 1 }}>
            <FormatColorTextIcon fontSize="small" />
            <Box sx={{ width: 18, height: 4, borderRadius: 0.5, bgcolor: currentValue, border: '1px solid', borderColor: 'divider' }} />
          </Stack>
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        disableAutoFocus
        disableEnforceFocus
      >
        <Box sx={{ p: 1.25, width: 190 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 0.5 }}>
            {TEXT_COLOR_PRESETS.map((preset) => {
              const selected = preset.toLowerCase() === currentValue;
              return (
                <Tooltip key={preset} title={preset.toUpperCase()}>
                  <Box
                    component="button"
                    type="button"
                    aria-label={preset}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => onPick(preset)}
                    sx={{
                      width: 24,
                      height: 24,
                      p: 0,
                      cursor: 'pointer',
                      borderRadius: 0.75,
                      bgcolor: preset,
                      border: '1px solid',
                      borderColor: selected ? 'secondary.main' : 'divider',
                      outline: selected ? '2px solid' : 'none',
                      outlineColor: 'secondary.main',
                      outlineOffset: '1px',
                    }}
                  />
                </Tooltip>
              );
            })}
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.25 }}>
            <Box
              component="input"
              type="color"
              value={currentValue}
              onChange={(event) => onPick(event.target.value)}
              sx={{ width: 34, height: 30, p: 0, border: 'none', bgcolor: 'transparent', cursor: 'pointer' }}
            />
            <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
              {currentValue}
            </Typography>
          </Stack>
        </Box>
      </Popover>
    </>
  );
};

const CustomReportExcelTemplateEditor = ({
  template,
  values,
  onChange,
  defaultLogoDataUri,
  onLogoUploaded,
}: CustomReportExcelTemplateEditorProps) => {
  const cellsByRef = React.useMemo(() => cellRecord(template), [template]);
  const sheetWidth = React.useMemo(() => totalSheetWidth(template), [template]);
  const width = React.useMemo(() => totalDisplayWidth(template), [template]);
  const height = React.useMemo(() => totalSheetHeight(template), [template]);
  const logo = React.useMemo(() => logoRect(template), [template]);
  const logoScale = normaliseLogoScale(values.logoScale);
  const scaledLogo = React.useMemo(() => (logo ? scaleLogoRect(logo, logoScale) : undefined), [logo, logoScale]);
  const hasLogo = Boolean(logo && (values.logoDataUri || defaultLogoDataUri));
  const [pdfBackgroundUrl, setPdfBackgroundUrl] = React.useState<string>();
  const [activePdfCellRef, setActivePdfCellRef] = React.useState<string>();
  const [checkboxFontSizePx, setCheckboxFontSizePx] = React.useState(14);
  const [textColor, setTextColor] = React.useState(DEFAULT_TEXT_COLOR);
  const [containerWidth, setContainerWidth] = React.useState(0);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  // Scale the fixed-width sheet down so it always fits the available width (no wide horizontal scroll).
  const fitScale = containerWidth > 0 ? Math.min(1, (containerWidth - 16) / width) : 1;
  const fitScaleRef = React.useRef(fitScale);
  fitScaleRef.current = fitScale;
  const savedRangeRef = React.useRef<Range | null>(null);
  const savedElementRef = React.useRef<HTMLElement | null>(null);
  const logoResizeRef = React.useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    startScale: number;
    baseWidth: number;
    baseHeight: number;
  }>();

  React.useEffect(() => {
    if (!values.pdfBackgroundDataUri) {
      setPdfBackgroundUrl(undefined);
      return undefined;
    }

    const nextUrl = URL.createObjectURL(dataUriToBlob(values.pdfBackgroundDataUri));
    setPdfBackgroundUrl(nextUrl);

    return () => {
      URL.revokeObjectURL(nextUrl);
    };
  }, [values.pdfBackgroundDataUri]);

  React.useEffect(() => {
    if (!activePdfCellRef) return;

    window.requestAnimationFrame(() => {
      const element = document.querySelector(
        `[data-custom-report-cell-ref="${activePdfCellRef}"]`,
      ) as HTMLElement | null;
      element?.focus();
    });
  }, [activePdfCellRef]);

  const updateRichCell = (ref: string, value: string, html: string) => onChange({
    ...values,
    cells: {
      ...values.cells,
      [ref]: value,
    },
    richCells: {
      ...(values.richCells ?? {}),
      [ref]: html,
    },
  });
  const syncEditableElement = (element: HTMLElement | null) => {
    const ref = element?.dataset.customReportCellRef;
    if (!element || !ref) return;
    updateRichCell(ref, element.innerText.replace(/\u00a0/g, ' '), element.innerHTML);
  };
  const selectedEditableElement = () => {
    const selection = window.getSelection();
    const node = selection?.anchorNode;
    const element = node instanceof HTMLElement ? node : node?.parentElement;
    return element?.closest('[data-custom-report-cell-ref]') as HTMLElement | null;
  };
  const selectedRangeInElement = (element: HTMLElement | null) => {
    const selection = window.getSelection();
    if (!element || !selection || selection.rangeCount === 0) return undefined;

    const range = selection.getRangeAt(0);
    if (!element.contains(range.commonAncestorContainer)) return undefined;
    return { range, selection };
  };
  const runEditorCommand = (command: string, value?: string) => {
    const element = selectedEditableElement();
    if (!element) return;
    document.execCommand(command, false, value);
    syncEditableElement(element);
  };
  const captureSelection = React.useCallback(() => {
    const element = selectedEditableElement();
    const context = selectedRangeInElement(element);
    if (!element || !context) return;

    savedRangeRef.current = context.range.cloneRange();
    savedElementRef.current = element;
    const node = context.selection.focusNode;
    const focusElement = node instanceof HTMLElement ? node : node?.parentElement;
    const target = focusElement && element.contains(focusElement) ? focusElement : element;
    const activeColor = normaliseCssColor(window.getComputedStyle(target).color);
    if (activeColor) setTextColor(activeColor);
  }, []);
  React.useEffect(() => {
    document.addEventListener('selectionchange', captureSelection);
    return () => document.removeEventListener('selectionchange', captureSelection);
  }, [captureSelection]);
  React.useEffect(() => {
    const element = scrollContainerRef.current;
    if (!element) return undefined;

    const updateWidth = () => setContainerWidth(element.clientWidth);
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const applyTextColor = (color: string) => {
    const normalised = normaliseCssColor(color) ?? color;
    setTextColor(normalised);

    const element = savedElementRef.current;
    const range = savedRangeRef.current;
    if (!element || !range || !element.isConnected) {
      runEditorCommand('foreColor', normalised);
      return;
    }

    element.focus();
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
    document.execCommand('foreColor', false, normalised);
    syncEditableElement(element);
    const nextRange = window.getSelection()?.rangeCount ? window.getSelection()?.getRangeAt(0).cloneRange() : undefined;
    if (nextRange) savedRangeRef.current = nextRange;
  };
  const styleSelectedInlineContent = (styles: Record<string, string>) => {
    const element = selectedEditableElement();
    const context = selectedRangeInElement(element);
    if (!element || !context || context.range.collapsed) return false;

    const span = document.createElement('span');
    Object.entries(styles).forEach(([property, value]) => span.style.setProperty(property, value));
    span.appendChild(context.range.extractContents());
    context.range.insertNode(span);

    const nextRange = document.createRange();
    nextRange.selectNodeContents(span);
    context.selection.removeAllRanges();
    context.selection.addRange(nextRange);
    syncEditableElement(element);
    return true;
  };
  const currentSelectionFontSize = (element: HTMLElement) => {
    const selection = window.getSelection();
    const node = selection?.focusNode;
    const focusElement = node instanceof HTMLElement ? node : node?.parentElement;
    const target = focusElement && element.contains(focusElement) ? focusElement : element;
    return normaliseCssFontSize(window.getComputedStyle(target).fontSize) ?? checkboxFontSizePx;
  };
  const adjustSelectedInlineSize = (delta: number) => {
    const element = selectedEditableElement();
    const baseSize = element ? currentSelectionFontSize(element) : checkboxFontSizePx;
    const nextSize = clampInlineFontSize(baseSize + delta);
    setCheckboxFontSizePx(nextSize);
    styleSelectedInlineContent({ 'font-size': `${nextSize}px` });
  };
  const clearSelectedCell = () => {
    const element = selectedEditableElement();
    if (!element) return;
    element.innerHTML = '';
    syncEditableElement(element);
    element.focus();
  };
  const insertCheckbox = (checked: boolean) => {
    const checkboxEntity = checked ? '&#x2611;' : '&#x2610;';
    runEditorCommand(
      'insertHTML',
      `<span style="font-size: ${checkboxFontSizePx}px; line-height: 1">${checkboxEntity}</span>&nbsp;`,
    );
  };
  const handlePdfSheetClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!template.pdfBackground) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const scale = fitScaleRef.current || 1;
    const ref = cellRefAtPoint(template, (event.clientX - rect.left) / scale, (event.clientY - rect.top) / scale);
    if (!ref) return;

    setActivePdfCellRef(ref);
    if (Object.prototype.hasOwnProperty.call(values.cells, ref)) return;

    onChange({
      ...values,
      cells: {
        ...values.cells,
        [ref]: '',
      },
      richCells: {
        ...(values.richCells ?? {}),
        [ref]: '',
      },
    });
  };
  const handleLogoUpload = async (file?: File) => {
    if (!file) return;
    const logoDataUri = await readFileAsDataUri(file);
    onChange({ ...values, logoDataUri, logoScale });
    try {
      await onLogoUploaded?.(logoDataUri);
    } catch {
      // Keep the report editor usable if the client logo update fails.
    }
  };
  const updateLogoScale = (nextScale: number) => {
    onChange({ ...values, logoScale: normaliseLogoScale(nextScale) });
  };
  const adjustLogoScale = (delta: number) => {
    updateLogoScale(logoScale + delta);
  };
  const handleLogoResizeStart = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!logo) return;

    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    logoResizeRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startScale: logoScale,
      baseWidth: logo.width,
      baseHeight: logo.height,
    };
  };
  const handleLogoResizeMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const resize = logoResizeRef.current;
    if (!resize || resize.pointerId !== event.pointerId) return;

    event.preventDefault();
    event.stopPropagation();
    const scale = fitScaleRef.current || 1;
    const widthDelta = ((event.clientX - resize.startX) / scale) / Math.max(1, resize.baseWidth);
    const heightDelta = ((event.clientY - resize.startY) / scale) / Math.max(1, resize.baseHeight);
    const dominantDelta = Math.abs(widthDelta) > Math.abs(heightDelta) ? widthDelta : heightDelta;
    updateLogoScale(resize.startScale + dominantDelta);
  };
  const handleLogoResizeEnd = (event: React.PointerEvent<HTMLDivElement>) => {
    const resize = logoResizeRef.current;
    if (!resize || resize.pointerId !== event.pointerId) return;

    event.preventDefault();
    event.stopPropagation();
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    logoResizeRef.current = undefined;
  };

  return (
    <Stack spacing={1.5}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2">{`${template.sheetName} - Excel Sheet`}</Typography>
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}>
          <ColorPickerButton value={textColor} onPick={applyTextColor} />
          <Tooltip title={i18n._('Insert unchecked checkbox')}>
            <IconButton
              size="small"
              color="secondary"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => insertCheckbox(false)}
            >
              <CheckBoxOutlineBlankIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={i18n._('Insert checked checkbox')}>
            <IconButton
              size="small"
              color="secondary"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => insertCheckbox(true)}
            >
              <CheckBoxIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={i18n._('Decrease selected checkbox or text size')}>
            <IconButton
              size="small"
              color="secondary"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => adjustSelectedInlineSize(-INLINE_FONT_SIZE_STEP)}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography variant="caption" sx={{ minWidth: 34, textAlign: 'center' }}>
            {`${checkboxFontSizePx}px`}
          </Typography>
          <Tooltip title={i18n._('Increase selected checkbox or text size')}>
            <IconButton
              size="small"
              color="secondary"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => adjustSelectedInlineSize(INLINE_FONT_SIZE_STEP)}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={i18n._('Clear selected cell')}>
            <IconButton
              size="small"
              color="secondary"
              onMouseDown={(event) => event.preventDefault()}
              onClick={clearSelectedCell}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button component="label" size="small" variant="outlined" color="secondary" startIcon={<UploadFileIcon />}>
            Logo
            <input hidden type="file" accept="image/png,image/jpeg,image/jpg" onChange={(event) => handleLogoUpload(event.target.files?.[0])} />
          </Button>
          {hasLogo && (
            <>
              <Tooltip title={i18n._('Decrease logo size')}>
                <IconButton
                  size="small"
                  color="secondary"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => adjustLogoScale(-LOGO_SCALE_STEP)}
                >
                  <RemoveIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Typography variant="caption" sx={{ minWidth: 42, textAlign: 'center' }}>
                {`${Math.round(logoScale * 100)}%`}
              </Typography>
              <Tooltip title={i18n._('Increase logo size')}>
                <IconButton
                  size="small"
                  color="secondary"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => adjustLogoScale(LOGO_SCALE_STEP)}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Stack>
      </Box>
      <Box ref={scrollContainerRef} sx={{ overflow: 'auto', bgcolor: '#e9edf3', borderRadius: 1, p: 2 }}>
        {/* Sizing wrapper reserves the scaled footprint so the parent never scrolls horizontally. */}
        <Box sx={{ width: width * fitScale, height: height * fitScale }}>
          <Box
            sx={{
              position: 'relative',
              width,
              minWidth: width,
              height,
              transform: `scale(${fitScale})`,
              transformOrigin: 'top left',
              bgcolor: '#fff',
              color: '#111827',
              fontFamily: 'Arial, sans-serif',
              boxShadow: '0 2px 14px rgba(15, 23, 42, 0.14)',
              border: '1px solid #e2e8f0',
            }}
          >
            {pdfBackgroundUrl && (
            <Box
              sx={{
                position: 'absolute',
                left: SHEET_LEFT_GUTTER,
                top: 0,
                width: sheetWidth,
                height,
                zIndex: 0,
                overflow: 'hidden',
                bgcolor: '#fff',
                pointerEvents: 'none',
              }}
            >
              <iframe
                title={`${template.label} PDF background`}
                src={`${pdfBackgroundUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 0,
                  pointerEvents: 'none',
                }}
              />
            </Box>
          )}
            {template.pdfBackground && (
            <Box
              onMouseDown={handlePdfSheetClick}
              sx={{
                position: 'absolute',
                left: SHEET_LEFT_GUTTER,
                top: 0,
                width: sheetWidth,
                height,
                zIndex: 2,
                cursor: 'text',
                bgcolor: 'transparent',
              }}
            />
          )}
            {scaledLogo && hasLogo && (
            <Box
              onMouseDown={(event) => event.stopPropagation()}
              sx={{
                position: 'absolute',
                left: SHEET_LEFT_GUTTER + scaledLogo.left,
                top: scaledLogo.top,
                width: scaledLogo.width,
                height: scaledLogo.height,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 0.75,
                pointerEvents: 'auto',
                zIndex: 5,
                border: '1px dashed',
                borderColor: 'secondary.main',
              }}
            >
              <img
                src={values.logoDataUri || defaultLogoDataUri}
                alt="Logo"
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  pointerEvents: 'none',
                  userSelect: 'none',
                }}
              />
              <Box
                title={i18n._('Drag to resize logo')}
                onPointerDown={handleLogoResizeStart}
                onPointerMove={handleLogoResizeMove}
                onPointerUp={handleLogoResizeEnd}
                onPointerCancel={handleLogoResizeEnd}
                sx={{
                  position: 'absolute',
                  right: -6,
                  bottom: -6,
                  width: 12,
                  height: 12,
                  borderRadius: '2px',
                  border: '1px solid',
                  borderColor: 'background.paper',
                  bgcolor: 'secondary.main',
                  cursor: 'nwse-resize',
                  boxShadow: 2,
                }}
              />
            </Box>
          )}
            {template.rows.map((row) => (
            template.columns.map((column) => {
              if (row.height <= 0 || column.width <= 0) return null;
              const ref = refFor(column, row);
              if (isCoveredByMerge(template, column.index, row.index)) return null;

              const merge = mergeForStart(template, ref);
              const columnSpan = merge ? merge.endColumn - merge.startColumn + 1 : 1;
              const rowSpan = merge ? merge.endRow - merge.startRow + 1 : 1;
              const cellWidth = template.columns
                .filter((item) => item.index >= column.index && item.index < column.index + columnSpan)
                .reduce((sum, item) => sum + columnWidth(item), 0);
              const cellHeight = template.rows
                .filter((item) => item.index >= row.index && item.index < row.index + rowSpan)
                .reduce((sum, item) => sum + rowHeight(item), 0);
              const cell = cellsByRef[ref];
              const style = template.styles[cell?.style ?? 0];
              const css = cssForStyle(style);
              const cellCss = template.pdfBackground
                ? {
                  ...css,
                  backgroundColor: 'transparent',
                  borderLeft: 'none',
                  borderRight: 'none',
                  borderTop: 'none',
                  borderBottom: 'none',
                  whiteSpace: 'nowrap' as const,
                }
                : css;
              const cellValue = values.cells[ref] ?? '';
              const hasCellText = Boolean(cellValue.trim());
              const isActivePdfCell = activePdfCellRef === ref;
              if (template.pdfBackground && !hasCellText && !isActivePdfCell) return null;
              const editableWidth = isSectionTitleCell(cellValue, style)
                ? Math.max(cellWidth, 420)
                : cellWidth;
              let cellZIndex = 1;
              if (template.pdfBackground) {
                cellZIndex = isActivePdfCell ? 4 : 3;
              } else if (hasCellText) {
                cellZIndex = 3;
              }

              return (
                <Box
                  key={ref}
                  sx={{
                    position: 'absolute',
                    left: SHEET_LEFT_GUTTER + columnOffset(template, column.index),
                    top: rowOffset(template, row.index),
                    width: cellWidth,
                    height: cellHeight,
                    boxSizing: 'border-box',
                    p: 0,
                    zIndex: cellZIndex,
                    overflow: template.pdfBackground || cellCss.whiteSpace !== 'pre-wrap' ? 'visible' : 'hidden',
                    ...cellCss,
                  }}
                >
                  <EditableCellText
                    cellRef={ref}
                    value={cellValue}
                    html={values.richCells?.[ref]}
                    onRichChange={(value, html) => updateRichCell(ref, value, html)}
                    cellWidth={editableWidth}
                    cellHeight={cellHeight}
                    css={cellCss}
                  />
                </Box>
              );
            })
          ))}
          </Box>
        </Box>
      </Box>
    </Stack>
  );
};

export default CustomReportExcelTemplateEditor;
